'use client';
import { IndexNotExist } from '@/app/constants/typedef';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import { CreateMedia, MediaOperation } from '@/app/models/media/IMedia';
import { Box } from '@mui/material';
import React, { useContext, useRef, useState } from 'react';
import { UserContext } from '@/app/contexts/user-context';

interface ImagePickerProps {
    onChange: (files: MediaOperation[]) => void;
    aspect?: number;
    allowMultiple?: boolean;
    children: React.ReactNode;
    disabled?: boolean;
}

const ImagePicker: React.FC<ImagePickerProps> = ({ onChange, aspect = 10 / 3.75, children, allowMultiple = false, disabled = false }) => {
    const [error, setError] = useState<string | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {addSnackbar} = useSnackbar();
    const userContext = useContext(UserContext);
    // const isDebugUser = userContext?.user?.id === 610;
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    const onDone = (d: MediaOperation[]) => {
        setFiles([]);
        if (onChange) {
            onChange(d);
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to convert file to base64'));
                }
            };
            reader.onerror = () => {
                reject(new Error('Error reading file'));
            };
            reader.readAsDataURL(file);
        });
    };

    // Read file into memory immediately to avoid Android file URI issues
    const readFileIntoMemory = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const arrayBuffer = reader.result as ArrayBuffer;
                    const blob = new Blob([arrayBuffer], { type: file.type });
                    const newFile = new File([blob], file.name, {
                        type: file.type,
                        lastModified: file.lastModified
                    });
                    resolve(newFile);
                } catch (error) {
                    console.error('Error creating new file from buffer:', error);
                    resolve(file); // Fallback to original file
                }
            };
            reader.onerror = () => {
                console.error('Error reading file into memory:', reader.error);
                resolve(file); // Fallback to original file
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const processFile = (file: File): Promise<MediaOperation> => {
        return new Promise<MediaOperation>(async (resolve) => {
            // On Android, read file into memory immediately to prevent URI expiration issues
            const fileToUse = isAndroid ? await readFileIntoMemory(file) : file;

            const res: CreateMedia & { preview?: string; width?: number; height?: number } = {
                file: fileToUse,
                mediaType: file.type.startsWith('image/') ? 'image' : 'video',
                order: 1,
                preview: undefined,
                width: undefined,
                height: undefined,
            };

            if (file.type.startsWith('image/')) {
                try {
                    // Convert file to base64 for preview
                    const base64 = await fileToBase64(fileToUse);
                    res.preview = base64;

                    // Get image dimensions
                    const img = new Image();
                    img.src = base64;
                    img.onload = () => {
                        res.width = img.width;
                        res.height = img.height;
                        resolve(res);
                    };
                    img.onerror = () => {
                        console.error('Image loading failed');
                        resolve(res);
                    };
                } catch (error) {
                    console.error('Error converting file to base64:', error);
                    resolve(res);
                }
            } else {
                // For video, still use object URL or convert to base64 if needed
                try {
                    const base64 = await fileToBase64(fileToUse);
                    res.preview = base64;
                } catch (error) {
                    console.error('Error converting video to base64:', error);
                }
                resolve(res);
            }
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles || selectedFiles.length < 1) {
            setError('File invalid');
            addSnackbar({
                message: '파일 형식이 올바르지 않습니다. 이미지 형식만 선택할 수 있습니다.',
                severity: 'error',
                autoHideDuration: 3000,
                horizontal: 'center',
                vertical: 'bottom',
                type: 'toast',
            });
            return;
        }

        const fileArray = Array.from(selectedFiles);
        
        // Validate file types
        if (
            fileArray.find(
                (file) => !file.type.startsWith('image/') && !file.type.startsWith('video/')
            )
        ) {
            setError('File invalid');
            addSnackbar({
                message: '파일 형식이 올바르지 않습니다. 이미지 또는 동영상 형식만 선택할 수 있습니다.',
                severity: 'error',
                autoHideDuration: 3000,
                horizontal: 'center',
                vertical: 'bottom',
                type: 'toast',
            });
            return;
        }

        const processedFiles = await Promise.all(fileArray.map(processFile));
        onDone(processedFiles);

        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        if (!disabled && fileInputRef.current) {
            e.stopPropagation();
            fileInputRef.current.click();
        }
    };

    return (
        <Box>
            <Box
                sx={{ zIndex: 99, cursor: disabled ? 'not-allowed' : 'pointer' }}
                onClick={handleClick}
            >
                {children}
            </Box>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,video/mp4,video/quicktime,video/webm,video/3gpp"
                multiple={allowMultiple}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={disabled}
            />
        </Box>
    );
};

export default ImagePicker;